import { create } from 'zustand';
import { searchApi } from '../api/search';
import type { SearchResponse, SearchSuggestion } from '@affogato/shared';

interface SearchState {
  query: string;
  results: SearchResponse['results'] | null;
  suggestions: SearchSuggestion[];
  isSearching: boolean;
  isOpen: boolean;
  error: string | null;

  // Actions
  setQuery: (query: string) => void;
  search: (query: string, type?: 'file' | 'folder' | 'client' | 'all') => Promise<void>;
  fetchSuggestions: (query: string) => Promise<void>;
  openSearch: () => void;
  closeSearch: () => void;
  clearResults: () => void;
  clearError: () => void;
}

export const useSearchStore = create<SearchState>((set, get) => ({
  query: '',
  results: null,
  suggestions: [],
  isSearching: false,
  isOpen: false,
  error: null,

  setQuery: (query) => {
    set({ query });
    if (query.length >= 1) {
      get().fetchSuggestions(query);
    } else {
      set({ suggestions: [] });
    }
  },

  search: async (query, type = 'all') => {
    if (query.length < 2) {
      set({ error: 'La búsqueda debe tener al menos 2 caracteres' });
      return;
    }

    set({ isSearching: true, error: null });
    try {
      const response = await searchApi.search(query, { type });
      set({
        results: response.results,
        isSearching: false,
      });
    } catch (error) {
      set({ error: 'Error en la búsqueda', isSearching: false });
    }
  },

  fetchSuggestions: async (query) => {
    try {
      const response = await searchApi.getSuggestions(query);
      set({ suggestions: response.suggestions });
    } catch (error) {
      // Silent fail for suggestions
    }
  },

  openSearch: () => set({ isOpen: true }),

  closeSearch: () => set({ isOpen: false, query: '', results: null, suggestions: [] }),

  clearResults: () => set({ results: null, query: '' }),

  clearError: () => set({ error: null }),
}));
